// ─── What: the probability panel ──────────────────────────────────────────────
//
// Three display modes share one panel because they share the scale, the group,
// the click target and the start-of-series markers: stacked areas, candlesticks
// for a single market, and the default line (optionally with its NO mirror).
//
// Returns the clipped group so the asset overlays can draw into the same one,
// which is what keeps them clipped to the plot and above the probability lines.

import * as d3 from 'd3';
import { alpha } from '@mui/material/styles';
import type { OHLCVPoint } from '../../../../api/types';
import { computeCandleGeometry, flipOutcomeSeries } from '../../chartCandles';
import { clampToDomain } from '../../chartYDomain';
import { clampLabelPos } from '../../chartAxis';
import { renderStackedAreas } from '../../chartStacked';
import {
    outcomeLabel,
    OUTCOME_MAXLEN_CANVAS,
} from '../../../../utils/outcomeLabels';
import { MARGIN } from '../layout';
import type { ChartGroup, DrawCtx } from '../drawContext';

export function drawProbabilityPanel(ctx: DrawCtx): ChartGroup {
    const {
        svg,
        layout,
        colors,
        theme,
        xScale,
        yScale,
        xDomain,
        probDomain,
        series,
        selectedIds,
        chartType,
        resolution,
        candleDataFor,
        colorMap,
        outcomesMap,
        noSideColor,
        hasHighlight,
        highlightedMarkets,
        onMarketClick,
        showNoLine,
    } = ctx;
    const { isDark, textColor } = colors;
    const w = layout.w;

    const probGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.prob})`) as ChartGroup;

    const lineGen = d3
        .line<OHLCVPoint>()
        .x(d => xScale(d.t))
        .y(d => yScale(d.close))
        .curve(d3.curveMonotoneX);

    if (chartType === 'stacked') {
        renderStackedAreas({
            group: probGroup,
            series,
            xScale,
            yScale,
            colorMap,
            othersColor: textColor,
            fallbackColor: theme.palette.primary.main,
        });
    }

    for (const s of series) {
        if (chartType === 'stacked') break;
        const color = colorMap.get(s.conditionId) ?? theme.palette.primary.main;
        const isHighlighted = !!(
            hasHighlight && highlightedMarkets!.has(s.conditionId)
        );
        const isOther = !!(
            hasHighlight && !highlightedMarkets!.has(s.conditionId)
        );

        // ── Candlestick mode (single market only) ──────────────────────────
        // Only the selected/visible market gets candles, keeping the renderer in
        // sync with the canShowCandles gate, which is computed from selectedIds.
        if (chartType === 'candles' && selectedIds.includes(s.conditionId)) {
            const geoms = computeCandleGeometry(
                candleDataFor(s),
                t => xScale(t),
                v => yScale(v),
                resolution
            );
            const upColor = theme.palette.success.main;
            const downColor = theme.palette.error.main;
            const cg = probGroup.append('g').attr('class', 'candles');
            for (const c of geoms) {
                cg.append('line')
                    .attr('x1', c.x)
                    .attr('x2', c.x)
                    .attr('y1', c.wickTop)
                    .attr('y2', c.wickBottom)
                    .attr('stroke', c.up ? upColor : downColor)
                    .attr('stroke-width', 1);
                cg.append('rect')
                    .attr('class', 'candle-body')
                    .attr('x', c.x - c.width / 2)
                    .attr('y', c.bodyY)
                    .attr('width', c.width)
                    .attr('height', c.bodyH)
                    .attr('fill', c.up ? upColor : downColor)
                    .style('cursor', 'pointer')
                    .on('click', () => onMarketClick?.(s.conditionId));
            }
            continue;
        }

        if (isHighlighted) {
            probGroup
                .append('path')
                .datum(s.data)
                .attr(
                    'd',
                    d3
                        .area<OHLCVPoint>()
                        .x(d => xScale(d.t))
                        .y0(yScale(probDomain.domain[0]))
                        .y1(d => yScale(d.close))
                        .curve(d3.curveMonotoneX)
                )
                .attr('fill', alpha(color, 0.08));
        }

        probGroup
            .append('path')
            .datum(s.data)
            .attr('d', lineGen)
            .attr('fill', 'none')
            .attr('stroke', color)
            .attr('stroke-width', isHighlighted ? 2.5 : isOther ? 1 : 1.8)
            .attr('opacity', isOther ? 0.3 : 1)
            .style('cursor', 'pointer')
            .on('click', () => onMarketClick?.(s.conditionId));

        // ── Series start marker ────────────────────────────────────────────
        // Only shown when the series starts WITHIN the visible range.
        const firstPt = s.data[0];
        const ftd = s.firstTradeDate;
        if (firstPt && ftd && ftd > xDomain[0]) {
            probGroup
                .append('circle')
                .attr('cx', xScale(firstPt.t))
                .attr('cy', yScale(clampToDomain(firstPt.close, probDomain.domain)))
                .attr('r', 3.5)
                .attr('fill', color)
                .attr('stroke', isDark ? '#000' : '#fff')
                .attr('stroke-width', 1.5)
                .attr('opacity', isOther ? 0.3 : 1)
                .style('pointer-events', 'none');
        }

        // ── NO-token mirror line ───────────────────────────────────────────
        if (showNoLine && s.conditionId === showNoLine && s.data.length > 0) {
            const noData: OHLCVPoint[] = flipOutcomeSeries(s.data);
            const lineOutcomes = outcomesMap.get(s.conditionId) ?? null;
            const noColor = noSideColor(lineOutcomes);
            const yesText =
                outcomeLabel(lineOutcomes, 0, OUTCOME_MAXLEN_CANVAS)?.text ??
                'YES';
            const noText =
                outcomeLabel(lineOutcomes, 1, OUTCOME_MAXLEN_CANVAS)?.text ??
                'NO';

            probGroup
                .append('path')
                .datum(noData)
                .attr('d', lineGen)
                .attr('fill', 'none')
                .attr('stroke', noColor)
                .attr('stroke-width', isHighlighted ? 2 : 1.4)
                .attr('stroke-dasharray', '6 4')
                .attr('opacity', 0.6)
                .style('pointer-events', 'none');

            // Half-width of centred 9px monospace text, for edge clamping.
            const halfTextWidth = (t: string) => (t.length * 5.4) / 2;

            const sideLabel = (
                pt: OHLCVPoint | undefined,
                text: string,
                fill: string
            ) => {
                if (!pt) return;
                probGroup
                    .append('text')
                    .attr(
                        'x',
                        clampLabelPos(
                            xScale(pt.t),
                            MARGIN.left,
                            MARGIN.left + w,
                            halfTextWidth(text)
                        )
                    )
                    .attr(
                        'y',
                        yScale(clampToDomain(pt.close, probDomain.domain)) - 6
                    )
                    .attr('fill', fill)
                    .attr('font-size', '9px')
                    .attr('font-family', 'monospace')
                    .attr('font-weight', 700)
                    .attr('text-anchor', 'middle')
                    .attr('opacity', 0.75)
                    .style('pointer-events', 'none')
                    .text(text);
            };

            sideLabel(s.data[0], yesText, color);
            sideLabel(noData[0], noText, noColor);
        }
    }

    return probGroup;
}
